/**
 * Terms Acceptance Utility
 * Manages terms acceptance tracking via backend API with localStorage as cache.
 * 
 * - acceptTerms: saves to backend AND localStorage (optimistic cache)
 * - hasAcceptedCurrentVersion: reads from localStorage cache (sync)
 * - syncTermsFromBackend: fetches from backend and hydrates localStorage
 */

import { api } from "../services/api";

export type TermsType = "shop" | "general";

export interface TermsAcceptance {
  termsType: TermsType;
  termsVersion: string;
  userName: string;
  userId: string;
  orgId?: string;
  acceptanceDate: string; // YYYY-MM-DD
  acceptanceTimestamp: string; // ISO 8601
}

interface TermsAcceptancesStorage {
  termsAcceptances: TermsAcceptance[];
}

// Current terms versions - update these when terms change
export const CURRENT_TERMS_VERSIONS: Record<TermsType, string> = {
  shop: "1.0",
  general: "2.0",
};

const STORAGE_KEY = "termsAcceptances";

// ============================================================================
// LOCAL CACHE (localStorage) — used for synchronous reads
// ============================================================================

/**
 * Get all terms acceptances from localStorage cache
 */
export const getAllAcceptances = (): TermsAcceptance[] => {
  if (typeof window === "undefined") return [];

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const data: TermsAcceptancesStorage = JSON.parse(stored);
    return data.termsAcceptances || [];
  } catch {
    return [];
  }
};

/**
 * Get acceptance for a specific terms type from localStorage cache
 */
export const getTermsAcceptance = (
  termsType: TermsType
): TermsAcceptance | null => {
  const acceptances = getAllAcceptances();
  return acceptances.find((acc) => acc.termsType === termsType) || null;
};

/**
 * Check if user has accepted the current version of terms (synchronous, reads cache)
 */
export const hasAcceptedCurrentVersion = (termsType: TermsType): boolean => {
  const acceptance = getTermsAcceptance(termsType);
  if (!acceptance) return false;

  const currentVersion = CURRENT_TERMS_VERSIONS[termsType];
  return acceptance.termsVersion === currentVersion;
};

/**
 * Check if user has accepted all required terms (both general and shop)
 */
export const hasAcceptedAllTerms = (): boolean => {
  return (
    hasAcceptedCurrentVersion("general") && hasAcceptedCurrentVersion("shop")
  );
};

/**
 * Save acceptance to localStorage cache
 */
const saveToLocalCache = (acceptance: TermsAcceptance): void => {
  const acceptances = getAllAcceptances();
  const filtered = acceptances.filter(
    (acc) => acc.termsType !== acceptance.termsType
  );

  const updated: TermsAcceptancesStorage = {
    termsAcceptances: [...filtered, acceptance],
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Ignore quota / storage errors.
  }
};

// ============================================================================
// BACKEND API — source of truth
// ============================================================================

/**
 * Accept all terms at once: saves both general and shop terms to backend, then caches in localStorage.
 * Falls back to localStorage-only if backend call fails.
 */
export const acceptAllTerms = async (
  userData: {
    userName: string;
    userId: string;
    orgId?: string;
  },
  token?: string | null
): Promise<TermsAcceptance[]> => {
  const acceptances: TermsAcceptance[] = [];
  
  // Accept both terms types
  for (const termsType of ["general", "shop"] as TermsType[]) {
    try {
      const acceptance = await acceptTerms(termsType, userData, token);
      acceptances.push(acceptance);
    } catch {
      // Continue with other terms types.
    }
  }
  
  return acceptances;
};

/**
 * Accept terms: saves to backend, then caches in localStorage.
 * Falls back to localStorage-only if backend call fails.
 */
export const acceptTerms = async (
  termsType: TermsType,
  userData: {
    userName: string;
    userId: string;
    orgId?: string;
  },
  token?: string | null
): Promise<TermsAcceptance> => {
  const now = new Date();
  const termsVersion = CURRENT_TERMS_VERSIONS[termsType];

  const acceptance: TermsAcceptance = {
    termsType,
    termsVersion,
    userName: userData.userName,
    userId: userData.userId,
    orgId: userData.orgId,
    acceptanceDate: now.toISOString().split("T")[0],
    acceptanceTimestamp: now.toISOString(),
  };

  // Try to save to backend first
  if (token && userData.orgId) {
    try {
      const response = await api.terms.accept(userData.orgId, token, {
        termsType,
        termsVersion,
        userName: userData.userName,
      });
      // Use the backend response to populate cache
      if (response.acceptance) {
        const backendAcceptance: TermsAcceptance = {
          termsType: response.acceptance.termsType as TermsType,
          termsVersion: response.acceptance.termsVersion,
          userName: response.acceptance.userName,
          userId: response.acceptance.userId,
          orgId: response.acceptance.orgId,
          acceptanceDate: response.acceptance.acceptanceDate,
          acceptanceTimestamp: response.acceptance.acceptanceTimestamp,
        };
        saveToLocalCache(backendAcceptance);
        return backendAcceptance;
      }
    } catch {
      // Fall back to localStorage-only below.
    }
  }

  // Fallback: save to localStorage only
  saveToLocalCache(acceptance);
  return acceptance;
};

/**
 * Sync terms acceptances from backend into localStorage cache.
 * Call this on login / app init to ensure localStorage is up-to-date.
 * Never throws; resolves `false` when the backend couldn't be reached.
 */
export const syncTermsFromBackend = async (
  orgId: string,
  token: string
): Promise<boolean> => {
  try {
    const response = await api.terms.getAll(orgId, token);

    if (response.acceptances && response.acceptances.length > 0) {
      const acceptances: TermsAcceptance[] = response.acceptances.map(
        (acc) => ({
          termsType: acc.termsType as TermsType,
          termsVersion: acc.termsVersion,
          userName: acc.userName,
          userId: acc.userId,
          orgId: acc.orgId,
          acceptanceDate: acc.acceptanceDate,
          acceptanceTimestamp: acc.acceptanceTimestamp,
        })
      );

      const updated: TermsAcceptancesStorage = {
        termsAcceptances: acceptances,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
    return true;
  } catch {
    // Use existing localStorage cache.
    return false;
  }
};

/**
 * Clear all terms acceptances (useful for testing or logout)
 */
export const clearAllAcceptances = (): void => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Clear acceptance for a specific terms type
 */
export const clearTermsAcceptance = (termsType: TermsType): void => {
  const acceptances = getAllAcceptances();
  const filtered = acceptances.filter((acc) => acc.termsType !== termsType);

  const updated: TermsAcceptancesStorage = {
    termsAcceptances: filtered,
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Ignore storage errors.
  }
};
